import React from "react";
import { formatNumber, resolveBetChangeRowCondition } from "../shared/utils/helpers";
import { useI18n } from "../providers/I18nProvider";
import { BetChangeRowCondition } from "../types/enums";

interface ConfirmBetChangesModalProps {
  onConfirm: () => void;
  onDecline: () => void;
  changes?: ModalBetChange[];
  originalStake: number;
  newStake: number;
  originalOdds?: number;
  newOdds?: number;
  oddsChanged?: boolean;
  stakeChanged?: boolean;
  exciseTaxValue?: number;
  exciseTaxRate?: number;
  eventName?: string;
  winningTax: number;
  newWinningTax?: number;
  maxPayout: number;
  newMaxPayout?: number;
  taxPercentage?: number;
  currency: string;
}

const getRowVisibility = (
  condition: BetChangeRowCondition,
): { showOddsRow: boolean; showStakeRow: boolean } => {
  switch (condition) {
    case BetChangeRowCondition.OddsOnly:
      return { showOddsRow: true, showStakeRow: false };
    case BetChangeRowCondition.StakeOnly:
      return { showOddsRow: false, showStakeRow: true };
    case BetChangeRowCondition.Both:
      return { showOddsRow: true, showStakeRow: true };
    case BetChangeRowCondition.None:
      return { showOddsRow: false, showStakeRow: false };
  }
};

export const ConfirmBetChangesModal: React.FC<ConfirmBetChangesModalProps> = ({
  onConfirm,
  onDecline,
  changes = [],
  originalStake,
  newStake,
  originalOdds = 0,
  newOdds = 0,
  oddsChanged = false,
  stakeChanged = false,
  exciseTaxValue = 0,
  exciseTaxRate = 0,
  eventName = "Event",
  winningTax,
  newWinningTax,
  maxPayout,
  newMaxPayout,
  taxPercentage = 0,
  currency,
}) => {
  const { t, tHtml } = useI18n();

  const displayWinningTax = newWinningTax ?? winningTax;
  const displayMaxPayout = newMaxPayout ?? maxPayout;

  const winningTaxBase = t("BetSlipWidgetWinningTax");
  const taxLabel = `${winningTaxBase} (${(taxPercentage * 100).toFixed(0)}%)`;

  const totalOddsLabel = t("BetSlipTotalOdds", { "totalOdds:span": "" }).trim();
  const totalStakeLabel = t("BetSlipTotalStake", { "receiptStake:span": "" }).trim();
  const originalLabel = t("BetSlipWidgetConfirmChangesOriginal");
  const newLabel = t("BetSlipWidgetConfirmChangesNew");

  const { showOddsRow: showAggregateOddsRow, showStakeRow: showAggregateStakeRow } =
    getRowVisibility(resolveBetChangeRowCondition(oddsChanged, stakeChanged));

  return (
    <div className="pt-6 px-4 pb-3">
      <div className="text-center text-25 mb-4">
        {t("BetSlipWidgetConfirmChangesTitle")}
      </div>

      {/* Event Changes */}
      {changes.length > 0 ? (
        <div className="mb-4">
          {changes.map((change, index) => {
            const resolvedOriginalStake = change.originalStake ?? originalStake;
            const resolvedNewStake = change.newStake ?? newStake;
            const { showOddsRow, showStakeRow } = getRowVisibility(
              resolveBetChangeRowCondition(
                !!change.oddsChanged,
                !!change.stakeChanged,
              ),
            );

            return (
              <div key={index} className="px-2">
                {/* Table */}
                <table className="w-full border-collapse mb-14">
                  {/* Header Row */}
                  <thead>
                    <tr className="h-[28px]">
                      <th className="px-4 text-left text-sm font-medium text-gray-800"></th>
                      <th className="pt-2 px-4 text-center text-sm font-medium text-gray-800">
                        {originalLabel}
                      </th>
                      <th className="pt-2 px-4 text-center text-sm font-medium text-gray-800">
                        {newLabel}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Odds Row */}
                    {showOddsRow && (
                      <tr className="h-[28px]">
                        <td className="border border-solid border-gray-800 px-4 py-1 text-sm font-medium text-center text-gray-800">
                          {totalOddsLabel}
                        </td>
                        <td className="border border-solid border-gray-800 px-4 py-1 text-center font-semibold text-gray-800">
                          {change.originalOdds.toFixed(2)}
                        </td>
                        <td className="border border-solid border-gray-800 px-4 py-1 text-center font-semibold text-gray-800">
                          {change.newOdds.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {/* Stake Row */}
                    {showStakeRow && (
                      <tr className="h-[28px]">
                        <td className="border border-solid border-gray-800 px-4 py-1 text-sm font-medium text-center text-gray-800">
                          {totalStakeLabel}
                        </td>
                        <td className="border border-solid border-gray-800 px-4 py-1 text-center font-semibold text-gray-800">
                          {resolvedOriginalStake.toFixed(2)} {currency}
                        </td>
                        <td className="border border-solid border-gray-800 px-4 py-1 text-center font-semibold text-gray-800">
                          {resolvedNewStake.toFixed(2)} {currency}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      ) : (
        /* Fallback when no changes array - use aggregate original vs new */
        <div className="px-2">
          {/* Table */}
          <table className="w-full border-collapse mb-14">
            {/* Header Row */}
            <thead>
              <tr className="h-[28px]">
                <th className="border border-solid border-gray-800 px-4 py-1 text-left text-sm font-medium text-gray-800"></th>
                <th className="border border-solid border-gray-800 px-4 py-1 text-center text-sm font-medium text-gray-800">
                  {originalLabel}
                </th>
                <th className="border border-solid border-gray-800 px-4 py-1 text-center text-sm font-medium text-gray-800">
                  {newLabel}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Odds Row */}
              {showAggregateOddsRow && (
                <tr className="h-[28px]">
                  <td className="border border-solid border-gray-800 px-4 py-1 text-sm font-medium text-gray-800">
                    {totalOddsLabel}
                  </td>
                  <td className="border border-solid border-gray-800 px-4 py-1 text-center font-semibold text-gray-800">
                    {originalOdds.toFixed(2)}
                  </td>
                  <td className="border border-solid border-gray-800 px-4 py-1 text-center font-semibold text-gray-800">
                    {newOdds.toFixed(2)}
                  </td>
                </tr>
              )}
              {/* Stake Row */}
              {showAggregateStakeRow && (
                <tr className="h-[28px]">
                  <td className="border border-solid border-gray-800 px-4 py-1 text-sm font-medium text-gray-800">
                    {totalStakeLabel}
                  </td>
                  <td className="border border-solid border-gray-800 px-4 py-1 text-center font-semibold text-gray-800">
                    {originalStake.toFixed(2)} {currency}
                  </td>
                  <td className="border border-solid border-gray-800 px-4 py-1 text-center font-semibold text-gray-800">
                    {newStake.toFixed(2)} {currency}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary - VAT, Winning Tax and Max Payout (new values after confirm) */}
      <div className="bg-gray-100 p-2 mb-9 space-y-1">
        {exciseTaxRate > 0 && (
          <div
            className="flex justify-between"
            dangerouslySetInnerHTML={tHtml("BetSlipExciseTax", {
              tax: exciseTaxRate,
              taxValue: formatNumber(exciseTaxValue),
              currency,
            })}
          />
        )}
        <div className="flex justify-between">
          <span className="text-sm text-gray-800">{taxLabel}</span>
          <span className="text-sm font-semibold text-gray-800">
            {displayWinningTax < 0 ? "-" : ""}
            {Math.abs(displayWinningTax).toFixed(2)} {currency}
          </span>
        </div>
        <div
          className="flex justify-between font-semibold [&_.maxPayout]:flex [&_.maxPayout]:w-full [&_.maxPayout]:justify-between"
          dangerouslySetInnerHTML={tHtml("BetSlipMaxPayOut", {
            maxPayOut: formatNumber(displayMaxPayout),
            currency,
          })}
        />
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={onConfirm}
          className="w-full px-6 text-22 h-[45px] bg-btn-continue-betting text-btn-continue-betting-text rounded-4"
        >
          {t("BetSlipWidgetConfirmAndPlaceBet")}
        </button>
        <button
          onClick={onDecline}
          className="w-full h-[45px] text-22 border border-danger text-danger rounded-4"
        >
          {t("BetSlipWidgetCancelBet")}
        </button>
      </div>
    </div>
  );
};

export default ConfirmBetChangesModal;
